/* track-trips.js — split a raw track (track/read points) into TRIPS and STOPS.
 *
 * The real go.lacak.io history breaks a track into per-trip segments ("Split by Stops") and
 * marks stop points. A stop is a run of consecutive stationary fixes (speed 0) lasting at least
 * `minStopMin` minutes; a trip is a maximal run of points that isn't a qualifying stop. Short
 * speed-0 blips (traffic lights) fold into the surrounding trip rather than splitting it.
 *
 * Points are the verbatim track/read shape: { lat, lng, speed, heading, time }.
 * Shared by TravelHistoryPanel (the trip list) and MonitoringView (the map colouring), so both
 * agree on trip boundaries. Pure + deterministic — no side effects.
 */

// Plausible Indonesian addresses for the trip/stop rows. The real go.lacak.io gets
// `start_address`/`end_address` per trip from the backend (track/list); the scaffold splits trips
// client-side, so we synthesise a stable, deterministic address per location here. Composed from
// the coords (street + house no. hashed at ~110 m resolution + a region-appropriate area) so
// nearby points read the same address, distant points differ, and the area roughly matches where
// the point actually is on the greater-Jakarta / West-Java map. Demo-only.
const STREETS = [
  'Sudirman', 'M.H. Thamrin', 'Gatot Subroto', 'Ahmad Yani', 'Diponegoro', 'Merdeka', 'Pahlawan',
  'Gajah Mada', 'Hayam Wuruk', 'Asia Afrika', 'Siliwangi', 'Otto Iskandardinata', 'Pemuda',
  'Veteran', 'Kartini', 'Cempaka Putih', 'Mangga Dua', 'Boulevard Raya', 'Margonda Raya', 'Kalimalang'
]
function areaFor(lat, lng) {
  if (lng > 108.2) return 'Cirebon'
  if (lng > 107.4) return 'Bandung'
  if (lat < -6.5) return 'Bogor'
  if (lng > 107.0) return 'Karawang'
  if (lng > 106.95) return 'Bekasi'
  if (lat < -6.35) return 'Depok'
  if (lng < 106.7) return 'Tangerang'
  if (lat < -6.25) return 'Jakarta Selatan'
  if (lat > -6.15) return 'Jakarta Utara'
  if (lng < 106.8) return 'Jakarta Barat'
  return 'Jakarta Pusat'
}
function addressFor(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return ''
  const a = Math.abs(Math.round(lat * 900)) // ~110 m cells
  const b = Math.abs(Math.round(lng * 900))
  const street = STREETS[(a + b * 7) % STREETS.length]
  const no = ((a * 13 + b * 17) % 240) + 1
  return 'Jl. ' + street + ' No. ' + no + ', ' + areaFor(lat, lng)
}

const R = 6371 // km, mean Earth radius
function haversineKm(a, b) {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const la1 = (a.lat * Math.PI) / 180
  const la2 = (b.lat * Math.PI) / 180
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

function ms(p) {
  const t = new Date(p.time).getTime()
  return Number.isFinite(t) ? t : 0
}

function tripMeta(points, startIdx, endIdx) {
  let distKm = 0
  let maxSpeed = 0
  let speedSum = 0
  for (let i = 0; i < points.length; i++) {
    const s = points[i].speed || 0
    if (s > maxSpeed) maxSpeed = s
    speedSum += s
    if (i > 0) distKm += haversineKm(points[i - 1], points[i])
  }
  const durationMs = Math.max(0, ms(points[points.length - 1]) - ms(points[0]))
  const first = points[0]
  const last = points[points.length - 1]
  return {
    startIdx,
    endIdx,
    from: first.time,
    to: last.time,
    count: points.length,
    distanceKm: distKm,
    durationMs,
    maxSpeed: Math.round(maxSpeed),
    avgSpeed: points.length ? Math.round(speedSum / points.length) : 0,
    start: { lat: first.lat, lng: first.lng },
    end: { lat: last.lat, lng: last.lng },
    // per-trip start/end addresses (real track/list start_address/end_address)
    startAddress: first.address || addressFor(first.lat, first.lng),
    endAddress: last.address || addressFor(last.lat, last.lng),
  }
}

// { trips: [...], stops: [...] }. `splitByStops=false` returns one trip spanning all points.
export function splitTrips(points, opts) {
  const o = opts || {}
  const minStopMs = (o.minStopMin != null ? o.minStopMin : 3) * 60000
  const splitByStops = o.splitByStops !== false
  const trips = []
  const stops = []
  const n = points ? points.length : 0
  if (!n) return { trips, stops }

  if (!splitByStops) {
    trips.push(tripMeta(points, 0, n - 1))
    return { trips, stops }
  }

  // Pass 1: flag qualifying stop runs (>= 2 fixes AND >= minStopMs).
  const inStop = new Array(n).fill(false)
  let i = 0
  while (i < n) {
    if ((points[i].speed || 0) === 0) {
      let j = i
      while (j < n && (points[j].speed || 0) === 0) j++
      const dur = ms(points[j - 1]) - ms(points[i])
      if (j - i >= 2 && dur >= minStopMs) {
        for (let k = i; k < j; k++) inStop[k] = true
        stops.push({
          startIdx: i,
          endIdx: j - 1,
          from: points[i].time,
          to: points[j - 1].time,
          durationMs: dur,
          lat: points[i].lat,
          lng: points[i].lng,
          address: points[i].address || addressFor(points[i].lat, points[i].lng),
        })
      }
      i = j
    } else {
      i++
    }
  }

  // Pass 2: trips = maximal runs of non-stop points (need >= 2 to draw a segment).
  i = 0
  while (i < n) {
    if (inStop[i]) { i++; continue }
    let j = i
    while (j < n && !inStop[j]) j++
    if (j - i >= 2) trips.push(tripMeta(points.slice(i, j), i, j - 1))
    i = j
  }

  // A track with NO qualifying stop still yields one trip over everything. But a fully-parked
  // window (pass 1 found a stop, pass 2 found no movement) must report zero trips — not a
  // phantom 0-km trip that contradicts the stop. So only fall back when there are no stops.
  if (!trips.length && !stops.length) trips.push(tripMeta(points, 0, n - 1))
  return { trips, stops }
}

// Distinct hue per trip for the "Color by tracks" mode (stable, high-contrast rotation).
export const TRIP_PALETTE = [
  '#1E88C7', '#E53935', '#8E44AD', '#00897B', '#F2960F',
  '#3949AB', '#7CB342', '#D81B60', '#00838F', '#5E35B1',
]
export function tripColor(index) {
  return TRIP_PALETTE[((index % TRIP_PALETTE.length) + TRIP_PALETTE.length) % TRIP_PALETTE.length]
}
